import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Select,
  Card,
  Row,
  Col,
  notification,
  Input,
  message,
  theme,
} from "antd";
import { MesServices } from "../Services/MesServices";
import dayjs from "dayjs";
import { AuditOutlined, PrinterOutlined } from "@ant-design/icons";
const { Option } = Select;

const Situation = () => {
  const [messageApi, messageHolder] = message.useMessage();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const numcont = localStorage.getItem("numcont");
  const mesServices = new MesServices();
  const currentYear = dayjs().year() || new Date().getFullYear(); // Année actuelle
  const [exercises, setExercises] = useState([]); // Liste des années
  const [selectedYear, setSelectedYear] = useState(currentYear || null); // Année sélectionnée, par défaut null si currentYear est undefined
  const [tableData, setTableData] = useState([]); // Données de la table
  const [loading, setLoading] = useState(false); // Indicateur de chargement
  const [isSelectTouched, setIsSelectTouched] = useState(false); // Indicateur si le Select a été utilisé
  const [filteredData, setFilteredData] = useState([]); // Données visibles après filtrage

  // Chargement des années pour le <Select>
  const loadExercises = async () => {
    try {
      const response = await mesServices.getComptaExercice(numcont);
      setExercises(response); // Met à jour les années récupérées
    } catch (error) {
      notification.error({
        message: "Erreur",
        description: "Impossible de récupérer les exercices.",
      });
    }
  };

  // Chargement des données de la table pour l'année sélectionnée
  const loadTableData = async () => {
    setLoading(true);
    try {
      const response = await mesServices.getSituationPart(
        numcont,
        selectedYear
      );
      setTableData(response); // Met à jour les données de la table
    } catch (error) {
      notification.error({
        message: "Erreur",
        description: "Impossible de récupérer les données de la table.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial des années et des données de la table
  useEffect(() => {
    loadExercises();
    loadTableData(); // Charge les données de l'année en cours
  }, []);

  // Recharge les données de la table lorsque l'année sélectionnée change
  useEffect(() => {
    if (isSelectTouched) {
      loadTableData();
    }
  }, [selectedYear]);

  const calculateTotals = (data) => {
    const totalFreels = data.reduce(
      (sum, item) => (item.debit !== 0 ? sum + (item.freels || 0) : sum),
      0
    );
    const totalDebit = data.reduce((sum, item) => sum + (item.debit || 0), 0);
    const totalCredit = data.reduce((sum, item) => sum + (item.credit || 0), 0);

    return {
      totalFreels,
      totalDebit,
      totalCredit,
    };
  };

  const { totalFreels, totalDebit, totalCredit } = calculateTotals(
    filteredData.length > 0 ? filteredData : tableData
  );

  const formatNumberWithSeparator = (number) => {
    return number.toLocaleString(); // Utilisez cette fonction pour formater les nombres
  };

  const handlePrint = async () => {
    const loadingMessage = messageApi.open({
      type: 'loading',
      content: 'Impression en cour..',
      duration: 0,
    });
    // Dismiss manually and asynchronously
    setTimeout(messageApi.destroy, 2500);
    const successMessage = messageApi.success({
      type: 'success',
      content: "Les documents ont été imprimer avec succés.",

    });

    try {
      const response = await mesServices.printParticipComptService(
        numcont,
        selectedYear
      );
      loadingMessage()
      successMessage()
      message.success("");
    } catch (error) {
      console.error("Erreur lors de l'impression :", error);
      message.error("Une erreur est survenue lors de l'impression.");
    }
  };

  const handleTableChange = (pagination, filters, sorter, extra) => {
    setFilteredData(extra.currentDataSource); // Met à jour les données visibles après filtrage
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "Date",
      dataIndex: "datecompta",
      key: "datecompta",
      align: "center",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"), // Formatage des dates
      sorter: (a, b) => a.datecompta - b.datecompta,
    },
    {
      title: "N° Document",
      dataIndex: "numdoc",
      key: "numdoc",
      //sorter: true,
      sorter: (a, b) => a.numdoc - b.numdoc,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8, width: 200 }}>
          {/* Recherche personnalisée */}
          <Input
            placeholder="Rechercher..."
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys([e.target.value])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8 }}
          />

          {/* Boutons */}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: "48%" }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: "48%" }}
            >
              Ok
            </Button>
          </div>
        </div>
      ),
      filters: [
        ...new Set(
          tableData.map((item) => item?.numdoc).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.numdoc === value,
    },
    {
      title: "Libellé",
      dataIndex: "libelle",
      key: "libelle",
      sorter: (a, b) => a.libelle - b.libelle,
    },
    {
      title: "Frais Réels",
      dataIndex: "freels",
      key: "freels",
      sorter: (a, b) => a.freels - b.freels,
      render: (text, record) => (
        <span style={{ color: "#1f4ea1", fontWeight: "bold", paddingRight: "10px" }}>
          {record.debit === 0 ? "" : formatNumberWithSeparator(text)}
        </span>
      ),
      align: "right",
    },
    {
      title: "QP Agent",
      dataIndex: "debit",
      key: "debit",
      sorter: (a, b) => a.debit - b.debit,
      render: (text) => (
        <strong style={{ paddingRight: 10, color: "red" }}>
          {text === 0 ? "" : formatNumberWithSeparator(text)}
        </strong>
      ),
      align: "right",
    },
    {
      title: "Remboursement",
      dataIndex: "credit",
      key: "credit",
      sorter: (a, b) => a.credit - b.credit,
      render: (text) => (
        <strong style={{ paddingRight: 10, color: "green" }}>
          {text === 0 ? "" : formatNumberWithSeparator(text)}
        </strong>
      ),
      align: "right",
    },
    {
      title: "Type Bénéf.",
      dataIndex: "typebenef",
      key: "typebenef",
      align: "center",
      sorter: (a, b) => a.typebenef - b.typebenef,
      render: (text) => text === "0" ? "" : text,
      filters: [
        ...new Set(
          tableData
            .map((item) => item?.typebenef ?? item?.typeBenef)
            .filter((value) => value !== null && value !== undefined && value !== "")
        ),
      ].map((value) => ({
        text: value,
        value: String(value),
      })),
      onFilter: (value, record) =>
        String(record.typebenef ?? record.typeBenef ?? "") === String(value),
    },
    {
      title: "Bénéficiaire",
      dataIndex: "benef",
      key: "benef",
      sorter: (a, b) => a.benef - b.benef,
      filters: [
        ...new Set(
          tableData.map((item) => item?.benef).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.benef === value,
    },
    {
      title: "Fournisseur",
      dataIndex: "fournisseur",
      key: "fournisseur",
      sorter: (a, b) => a.fournisseur - b.fournisseur,
      filters: [
        ...new Set(
          tableData.map((item) => item?.fournisseur).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.fournisseur === value,
    },
  ];

  return (
    <div
      style={{
        padding: "20px",
        background: "linear-gradient(115deg, #eef2fc 0%, #e4f5ec 38%, #f9eedd 100%)",
        minHeight: "100vh",
        transition: "background 0.7s ease-in-out"
      }}
    >
      {messageHolder}
      <h2 style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: 10 }}>
        <AuditOutlined style={{ color: "#1f4ea1", fontSize: 28 }} />
        Ma Situation
      </h2>
      <Card
        hoverable
        bordered={false}
        style={{
          marginBottom: "20px",
          textAlign: "center",
          borderRadius: 12,
          background: "linear-gradient(135deg, #f6ffed, #d9f7be)",
          boxShadow: "0 6px 16px rgba(82, 196, 26, 0.18)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #52c41a, #73d13d)",
              boxShadow: "0 4px 12px rgba(82,196,26,0.35)",
              color: "#fff",
              fontSize: 22,
            }}
            aria-hidden
          >
            ₣
          </span>
          <div>
            <div style={{ fontSize: 18, color: "#3f8600", letterSpacing: 0.3 }}>Solde disponible</div>
            <div style={{ fontSize: 30, fontWeight: 800, color: "#135200", lineHeight: 1.1 }}>
              {formatNumberWithSeparator(totalDebit - totalCredit)} CFA
            </div>
          </div>
        </div>
      </Card>

      <Card
        hoverable
        bordered={false}
        // title={<span style={{ textAlign: "left", display: "block" }}>Détails de ma situation</span>}
        style={{ borderRadius: borderRadiusLG, backgroundColor: colorBgContainer }}
      >
        <Row justify="space-between" style={{ marginBottom: "10px" }}>
          <Col>
            <Select
              style={{ width: 200, marginBottom: 20 }}
              value={selectedYear}
              onChange={(value) => {
                setSelectedYear(value);
                setIsSelectTouched(true);
              }}
              placeholder="Exercice"
            >
              {exercises.map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<PrinterOutlined />}
              className="buttonExport"
              onClick={handlePrint}
            >
              Imprimer Extrait
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={tableData}
          loading={loading}
          scroll={{ x: true }}
          rowKey="numligne"
          bordered={false}
          size="small"
          style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
          className="custom-header-table my-custom-table situation-table"
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          onChange={handleTableChange}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3} />
              <Table.Summary.Cell index={1} align="right">
                <strong style={{ color: "#1f4ea1", fontWeight: 700 }}>
                  {formatNumberWithSeparator(totalFreels)}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <strong style={{ color: "#ff4d4f", fontWeight: 700 }}>
                  {formatNumberWithSeparator(totalDebit)}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <strong style={{ color: "#52c41a", fontWeight: 700 }}>
                  {formatNumberWithSeparator(totalCredit)}
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>
    </div>
  );
};

export default Situation;
